import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import budgetService from '../../../../services/budgetService';
import categoryService from '../../../transactions/services/categoryService';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Switch,
  Divider,
  Grid,
  CircularProgress,
  Paper,
  Autocomplete,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Select,
  InputLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';

const TemplateForm = () => {
  const { id, budgetId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const isFromBudget = !!budgetId;
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateType: 'personal',
    isDefault: false,
    categories: [],
    income: [],
    tags: []
  });
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tagOptions, setTagOptions] = useState([]);
  const [sourceBudget, setSourceBudget] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  
  useEffect(() => {
    fetchAvailableCategories();
    
    if (isEdit) {
      fetchTemplate();
    } else if (isFromBudget) {
      fetchBudgetForTemplate();
    }
  }, [id, budgetId]);
  
  const fetchAvailableCategories = async () => {
    try {
      const response = await categoryService.getAllCategories();
      setAvailableCategories(response.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError('Could not load available categories. Some features may be limited.');
    }
  };
  
  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const template = await budgetService.getTemplateById(id);
      setFormData({
        name: template.name || '',
        description: template.description || '',
        templateType: template.templateType || 'personal',
        isDefault: template.isDefault || false,
        categories: template.categories || [],
        income: template.income || [],
        tags: template.tags || []
      });
      
      // Collect unique tags from this template for autocomplete
      if (template.tags && template.tags.length > 0) {
        setTagOptions(template.tags);
      }
    } catch (err) {
      console.error('Failed to fetch template:', err);
      setError('Could not load template. Please try again or go back to templates list.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBudgetForTemplate = async () => {
    try {
      setLoading(true);
      const budget = await budgetService.getBudgetById(budgetId);
      setSourceBudget(budget);
      
      setFormData({
        name: `${budget.name} Template`,
        description: `Template created from budget: ${budget.name}`,
        templateType: 'personal',
        isDefault: false,
        categories: budget.categories || [],
        income: budget.income || [],
        tags: []
      });
    } catch (err) {
      console.error('Failed to fetch budget:', err);
      setError('Could not load budget data. Please try again or create a template from scratch.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name === 'isDefault') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear any previous success/error messages when form is modified
    setSuccess('');
    setError('');
  };
  
  const handleAddTag = () => {
    if (tagInput && !formData.tags.includes(tagInput)) {
      const newTags = [...formData.tags, tagInput];
      setFormData(prev => ({
        ...prev,
        tags: newTags
      }));
      
      // Add to options if it's a new tag
      if (!tagOptions.includes(tagInput)) {
        setTagOptions(prev => [...prev, tagInput]);
      }
      
      setTagInput('');
    }
  };
  
  const handleDeleteTag = (tagToDelete) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Template name is required');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      let result;
      
      if (isEdit) {
        result = await budgetService.updateTemplate(id, formData);
        setSuccess('Template updated successfully');
      } else if (isFromBudget) {
        result = await budgetService.createTemplateFromBudget(budgetId, formData);
        setSuccess('Template created successfully from budget');
      } else {
        result = await budgetService.createTemplate(formData);
        setSuccess('Template created successfully');
      }
      
      // After success, redirect after a short delay to show the success message
      setTimeout(() => {
        navigate('/budgeting/templates');
      }, 1500);
    } catch (err) {
      console.error('Failed to save template:', err);
      setError('Failed to save template. Please check your inputs and try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    if (formData.name || formData.description || formData.tags.length > 0) {
      setOpenConfirmDialog(true);
    } else {
      navigate('/budgeting/templates');
    }
  };
  
  const handleConfirmCancel = () => {
    setOpenConfirmDialog(false);
    navigate('/budgeting/templates');
  };
  
  const handleCloseDialog = () => {
    setOpenConfirmDialog(false);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton 
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Edit Budget Template' : isFromBudget ? 'Create Template from Budget' : 'Create Budget Template'}
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      {isFromBudget && sourceBudget && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Creating template from budget: {sourceBudget.name}
        </Alert>
      )}
      
      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="name"
              label="Template Name"
              fullWidth
              required
              value={formData.name}
              onChange={handleChange}
              error={!formData.name.trim()}
              helperText={!formData.name.trim() ? 'Name is required' : ''}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="template-type-label">Template Type</InputLabel>
              <Select
                labelId="template-type-label"
                id="templateType"
                name="templateType"
                value={formData.templateType}
                label="Template Type"
                onChange={handleChange}
              >
                <MenuItem value="personal">Personal</MenuItem>
                <MenuItem value="household">Household</MenuItem>
                <MenuItem value="special">Special Purpose</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="description"
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isDefault}
                  onChange={handleChange}
                  name="isDefault"
                  color="primary"
                />
              }
              label={`Set as default template for ${formData.templateType} budgets`}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Autocomplete
                freeSolo
                options={tagOptions}
                inputValue={tagInput}
                onInputChange={(event, newValue) => {
                  setTagInput(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add Tags"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
                sx={{ flexGrow: 1, mr: 1 }}
              />
              <Button
                variant="outlined"
                onClick={handleAddTag}
                disabled={!tagInput}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                />
              ))}
              {formData.tags.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No tags added yet. Tags help you organize and find templates more easily.
                </Typography>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<ArrowBackIcon />}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting || !formData.name.trim()}
                startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {submitting ? 'Saving...' : isEdit ? 'Update Template' : 'Save Template'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Discard Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to leave without saving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Continue Editing</Button>
          <Button onClick={handleConfirmCancel} color="primary" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateForm; 